import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateMemberNeoDefaultInputMode, useUpdateMemberObservationNewThresholds, useUser } from '@/hooks';
import { NeoInputMode } from '@vivport/backend/types';
import { toast } from 'sonner';
import { SettingsPage, SettingsRow, SettingsRowList, SettingsSection } from './SettingsPage';

const INPUT_MODE_OPTIONS: { value: NeoInputMode; label: string; description: string }[] = [
  {
    value: NeoInputMode.VOICE,
    label: 'Voice',
    description:
      'Neo greets you and the conversation starts listening straight away. Your browser will ask for microphone access.',
  },
  {
    value: NeoInputMode.DICTATION,
    label: 'Dictation',
    description: 'The microphone opens and types what you say into the composer for you to review before sending.',
  },
  {
    value: NeoInputMode.TEXT,
    label: 'Text',
    description: 'The composer opens ready to type. Nothing listens until you start it yourself.',
  },
];

const OFF_VALUE = 'off';
const THRESHOLD_CHOICES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

const THRESHOLD_LEVERS = [
  { key: 'observationNewAfterRecordings', label: 'Recordings' },
  { key: 'observationNewAfterPhotos', label: 'Photos' },
  { key: 'observationNewAfterVideos', label: 'Videos' },
] as const;

export function NeoSection() {
  const { data: memberContext } = useUser();
  const updateInputModeMutation = useUpdateMemberNeoDefaultInputMode();
  const updateThresholdsMutation = useUpdateMemberObservationNewThresholds();

  const selectedMember = memberContext?.selectedMember;

  const handleInputModeChange = (value: string) => {
    const option = INPUT_MODE_OPTIONS.find((candidate) => candidate.value === value);
    if (!selectedMember || !option) {
      return;
    }
    updateInputModeMutation.mutate(
      { memberId: selectedMember.id, neoDefaultInputMode: option.value },
      {
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to update how Neo opens');
        },
      }
    );
  };

  const thresholds = {
    observationNewAfterRecordings: selectedMember?.observationNewAfterRecordings ?? null,
    observationNewAfterPhotos: selectedMember?.observationNewAfterPhotos ?? null,
    observationNewAfterVideos: selectedMember?.observationNewAfterVideos ?? null,
  };

  // One policy, one call: every change sends all three so a partial save can never leave
  // the levers disagreeing.
  const handleThresholdChange = (key: (typeof THRESHOLD_LEVERS)[number]['key'], value: string) => {
    if (!selectedMember) {
      return;
    }
    updateThresholdsMutation.mutate(
      {
        memberId: selectedMember.id,
        ...thresholds,
        [key]: value === OFF_VALUE ? null : Number(value),
      },
      {
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to update when a new Observation starts');
        },
      }
    );
  };

  return (
    <SettingsPage
      title="Neo"
      description="How a fresh chat opens and when Observation Capture starts a new Observation."
    >
      <SettingsSection
        title="Opening mode"
        description="How a fresh chat opens when you press Speak2Me. Resuming an existing conversation never starts a mode on its own."
      >
        <RadioGroup
          value={selectedMember?.neoDefaultInputMode ?? NeoInputMode.VOICE}
          onValueChange={handleInputModeChange}
          disabled={!selectedMember || updateInputModeMutation.isPending}
          className="gap-0 divide-y"
        >
          {INPUT_MODE_OPTIONS.map((option) => (
            <SettingsRow
              key={option.value}
              as="div"
              leading={<RadioGroupItem value={option.value} id={`neo-input-mode-${option.value}`} />}
              title={
                <Label htmlFor={`neo-input-mode-${option.value}`} className="cursor-pointer">
                  {option.label}
                </Label>
              }
              detail={option.description}
            />
          ))}
        </RadioGroup>
      </SettingsSection>

      <SettingsSection
        title="New observation"
        description="Observation Capture keeps adding to the Observation you are filling. Set how much one Observation should hold before the next capture starts a fresh one. Every threshold you set has to be reached. Off for all three keeps adding to the same Observation, and you can always pick an earlier Observation from the list."
      >
        <SettingsRowList>
          {THRESHOLD_LEVERS.map((lever) => (
            <SettingsRow
              key={lever.key}
              title={<Label htmlFor={`neo-${lever.key}`}>{lever.label}</Label>}
              trailing={
                <Select
                  value={thresholds[lever.key] === null ? OFF_VALUE : String(thresholds[lever.key])}
                  onValueChange={(value) => handleThresholdChange(lever.key, value)}
                  disabled={!selectedMember || updateThresholdsMutation.isPending}
                >
                  <SelectTrigger id={`neo-${lever.key}`} className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={OFF_VALUE}>Off</SelectItem>
                    {THRESHOLD_CHOICES.map((choice) => (
                      <SelectItem key={choice} value={String(choice)}>
                        {choice}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              }
            />
          ))}
        </SettingsRowList>
      </SettingsSection>
    </SettingsPage>
  );
}
